import {
    type ChangeEvent,
    type ClipboardEvent,
    type KeyboardEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { ArrowLeft, ScanLine, Shield, ShieldAlert } from 'lucide-react';
import { Alert, Button, Modal } from '../../../components/ui';
import { useToast } from '../../../hooks';
import type { SecurityPinType } from '../../../types/tracking';
import { QrScanError, scanQrOnce } from './qrScanner';
import { useSecurityPinVerification } from './useSecurityPinVerification';

interface SecurityPinScannerModalProps {
    isOpen: boolean;
    onClose: () => void;
    contractId: number;
    type: SecurityPinType;
    maxAttempts?: number;
    onVerified?: () => void | Promise<void>;
}

const PIN_LENGTH = 4;
const EMPTY_DIGITS = Array.from({ length: PIN_LENGTH }, () => '');

function pinTypeLabel(type: SecurityPinType): string {
    return type === 'pickup' ? 'recoleccion' : 'entrega';
}

function pinTypeUpper(type: SecurityPinType): string {
    return type === 'pickup' ? 'RECOLECCIÓN' : 'ENTREGA';
}

export function SecurityPinScannerModal({
    isOpen,
    onClose,
    contractId,
    type,
    maxAttempts = 5,
    onVerified,
}: SecurityPinScannerModalProps) {
    const { success, error: showError, showToast } = useToast();
    const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
    const [digits, setDigits] = useState<string[]>(EMPTY_DIGITS);
    const [shouldShake, setShouldShake] = useState(false);
    const [isScanning, setIsScanning] = useState(false);
    const [isCompletingVerification, setIsCompletingVerification] = useState(false);
    const {
        verifyByPin,
        verifyByQrToken,
        isSubmitting,
        lastError,
        remainingAttempts,
        isLocked,
        reset,
    } = useSecurityPinVerification(contractId, type);

    useEffect(() => {
        if (!isOpen) {
            setDigits(EMPTY_DIGITS);
            setShouldShake(false);
            setIsCompletingVerification(false);
            reset();
            return;
        }

        const focusTimer = window.setTimeout(() => {
            inputsRef.current[0]?.focus();
        }, 50);

        return () => {
            window.clearTimeout(focusTimer);
        };
    }, [isOpen, reset]);

    useEffect(() => {
        if (!shouldShake) {
            return;
        }

        const timer = window.setTimeout(() => setShouldShake(false), 600);
        return () => window.clearTimeout(timer);
    }, [shouldShake]);

    const isComplete = digits.every((digit) => digit !== '');
    const remainingDisplay = remainingAttempts ?? maxAttempts;
    const isBusy = isSubmitting || isCompletingVerification;

    const triggerErrorFeedback = useCallback(() => {
        setShouldShake(true);
        setDigits(EMPTY_DIGITS);
        window.setTimeout(() => inputsRef.current[0]?.focus(), 50);
    }, []);

    const completeVerifiedFlow = useCallback(async () => {
        success(`PIN de ${pinTypeLabel(type)} verificado.`, 'Validación exitosa');
        setIsCompletingVerification(true);

        try {
            await onVerified?.();
            onClose();
        } catch {
            showError('El PIN fue validado, pero no se pudo completar la confirmación automática.', 'Confirmación pendiente');
        } finally {
            setIsCompletingVerification(false);
        }
    }, [onClose, onVerified, showError, success, type]);

    const handleSubmitPin = useCallback(async () => {
        if (!isComplete || isBusy || isLocked) {
            return;
        }

        const pin = digits.join('');
        const result = await verifyByPin(pin);

        if (result.ok) {
            await completeVerifiedFlow();
            return;
        }

        if (result.error.kind === 'invalid_pin' || result.error.kind === 'invalid_qr') {
            triggerErrorFeedback();
            return;
        }

        if (result.error.kind === 'locked') {
            showToast({
                tone: 'error',
                title: 'PIN bloqueado',
                message:
                    'Demasiados intentos. Se generó un nuevo PIN y se notificó al cliente.',
            });
            return;
        }

        showError(result.error.message, 'No se pudo validar el PIN');
    }, [
        completeVerifiedFlow,
        digits,
        isBusy,
        isComplete,
        isLocked,
        showError,
        showToast,
        triggerErrorFeedback,
        verifyByPin,
    ]);

    const handleScanQr = useCallback(async () => {
        if (isBusy || isLocked) {
            return;
        }

        setIsScanning(true);

        try {
            const scan = await scanQrOnce();

            if (!scan) {
                return;
            }

            const result = await verifyByQrToken(scan.value);

            if (result.ok) {
                await completeVerifiedFlow();
                return;
            }

            if (result.error.kind === 'invalid_qr' || result.error.kind === 'invalid_pin') {
                showError(result.error.message, 'QR inválido');
                return;
            }

            if (result.error.kind === 'locked') {
                showToast({
                    tone: 'error',
                    title: 'PIN bloqueado',
                    message:
                        'Demasiados intentos. Se generó un nuevo PIN y se notificó al cliente.',
                });
                return;
            }

            showError(result.error.message, 'No se pudo validar el QR');
        } catch (scanError) {
            const message =
                scanError instanceof QrScanError
                    ? scanError.message
                    : 'No se pudo abrir el escáner.';
            showError(message, 'Escáner no disponible');
        } finally {
            setIsScanning(false);
        }
    }, [
        completeVerifiedFlow,
        isBusy,
        isLocked,
        showError,
        showToast,
        verifyByQrToken,
    ]);

    const handleDigitChange = useCallback(
        (index: number, event: ChangeEvent<HTMLInputElement>) => {
            const raw = event.target.value.replace(/\D/g, '');

            if (!raw) {
                setDigits((current) => {
                    const next = [...current];
                    next[index] = '';
                    return next;
                });
                return;
            }

            if (raw.length === PIN_LENGTH) {
                setDigits(raw.split('').slice(0, PIN_LENGTH));
                window.setTimeout(() => inputsRef.current[PIN_LENGTH - 1]?.focus(), 0);
                return;
            }

            const value = raw[raw.length - 1];

            setDigits((current) => {
                const next = [...current];
                next[index] = value;
                return next;
            });

            if (index < PIN_LENGTH - 1) {
                window.setTimeout(() => inputsRef.current[index + 1]?.focus(), 0);
            }
        },
        [],
    );

    const handleKeyDown = useCallback(
        (index: number, event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter') {
                event.preventDefault();
                void handleSubmitPin();
                return;
            }

            if (event.key === 'Backspace' && !digits[index] && index > 0) {
                event.preventDefault();
                setDigits((current) => {
                    const next = [...current];
                    next[index - 1] = '';
                    return next;
                });
                window.setTimeout(() => inputsRef.current[index - 1]?.focus(), 0);
                return;
            }

            if (event.key === 'ArrowLeft' && index > 0) {
                event.preventDefault();
                inputsRef.current[index - 1]?.focus();
                return;
            }

            if (event.key === 'ArrowRight' && index < PIN_LENGTH - 1) {
                event.preventDefault();
                inputsRef.current[index + 1]?.focus();
            }
        },
        [digits, handleSubmitPin],
    );

    const handlePaste = useCallback(
        (event: ClipboardEvent<HTMLInputElement>) => {
            const text = event.clipboardData.getData('text').replace(/\D/g, '');

            if (text.length === 0) {
                return;
            }

            event.preventDefault();
            const next = text.split('').slice(0, PIN_LENGTH);

            while (next.length < PIN_LENGTH) {
                next.push('');
            }

            setDigits(next);
            window.setTimeout(
                () => inputsRef.current[Math.min(text.length, PIN_LENGTH) - 1]?.focus(),
                0,
            );
        },
        [],
    );

    const inlineError = useMemo(() => {
        if (!lastError) {
            return null;
        }

        if (lastError.kind === 'invalid_pin') {
            const remaining = lastError.remainingAttempts ?? remainingDisplay;
            return `PIN incorrecto. Intentos restantes: ${remaining}`;
        }

        if (lastError.kind === 'invalid_qr') {
            return lastError.message;
        }

        if (lastError.kind === 'no_active_pin') {
            return 'No hay PIN vigente. Pide al cliente que reenvíe el PIN.';
        }

        if (lastError.kind === 'locked') {
            return 'Demasiados intentos. Se generó un nuevo PIN y se notificó al cliente.';
        }

        if (lastError.kind === 'forbidden') {
            return lastError.message;
        }

        if (lastError.kind === 'rate_limited') {
            return lastError.message;
        }

        return lastError.message;
    }, [lastError, remainingDisplay]);

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="medium" closeOnBackdrop={false}>
            <div className="flex flex-col gap-4 p-5">
                <header className="flex items-center justify-between gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex size-9 items-center justify-center rounded-full text-text-muted transition hover:bg-background"
                        aria-label="Cerrar"
                    >
                        <ArrowLeft className="h-5 w-5" aria-hidden="true" />
                    </button>
                    <h2
                        id={`security-pin-scanner-title-${type}`}
                        className="text-sm font-semibold uppercase tracking-wide text-text"
                    >
                        Validar PIN · {pinTypeUpper(type)}
                    </h2>
                    <span className="size-9" aria-hidden="true" />
                </header>

                <p className="text-sm text-text-muted">
                    Pide el PIN o QR del punto. Al validarlo se confirmara automaticamente la llegada.
                </p>

                <fieldset
                    className="space-y-3"
                    aria-labelledby={`security-pin-scanner-title-${type}`}
                    disabled={isLocked || isBusy}
                >
                    <legend className="sr-only">Ingresar PIN de {pinTypeLabel(type)}</legend>

                    <div
                        className={`flex items-center justify-center gap-3 ${shouldShake ? 'animate-pulse' : ''}`}
                        aria-invalid={Boolean(lastError && lastError.kind === 'invalid_pin')}
                    >
                        {digits.map((digit, index) => (
                            <input
                                key={`pin-digit-${type}-${index}`}
                                ref={(node) => {
                                    inputsRef.current[index] = node;
                                }}
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                maxLength={1}
                                autoComplete="one-time-code"
                                value={digit}
                                onChange={(event) => handleDigitChange(index, event)}
                                onKeyDown={(event) => handleKeyDown(index, event)}
                                onPaste={handlePaste}
                                aria-label={`Dígito ${index + 1} del PIN`}
                                className="h-16 w-12 rounded-2xl border border-border bg-background text-center text-2xl font-semibold text-text shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
                            />
                        ))}
                    </div>

                    <Button
                        type="button"
                        variant="primary"
                        className="w-full"
                        onClick={() => void handleSubmitPin()}
                        disabled={!isComplete || isBusy || isLocked}
                        isLoading={isBusy}
                    >
                        <Shield className="h-4 w-4" aria-hidden="true" />
                        {isCompletingVerification ? 'Confirmando llegada' : 'Validar PIN'}
                    </Button>
                </fieldset>

                <div className="flex items-center gap-3 text-xs text-text-muted">
                    <span className="h-px flex-1 bg-border" aria-hidden="true" />
                    o
                    <span className="h-px flex-1 bg-border" aria-hidden="true" />
                </div>

                <Button
                    type="button"
                    variant="secondary"
                    className="w-full"
                    onClick={() => void handleScanQr()}
                    disabled={isScanning || isBusy || isLocked}
                    isLoading={isScanning}
                >
                    <ScanLine className="h-4 w-4" aria-hidden="true" />
                    Escanear QR
                </Button>

                {inlineError ? (
                    <Alert type={lastError?.kind === 'locked' ? 'error' : 'warning'}>
                        <div className="flex items-start gap-2">
                            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                            <span>{inlineError}</span>
                        </div>
                    </Alert>
                ) : null}

                <p className="text-center text-xs text-text-muted">
                    Intentos restantes: {isLocked ? 0 : remainingDisplay}
                </p>
            </div>
        </Modal>
    );
}
